// ============================================================================
// marketDisputeMessages.ts — ФОРМАТ сообщений спора (SSoT разметки).
//
// Открытие спора и решение оператора — обычные сообщения обсуждения заявки с
// машиночитаемой первой строкой. Здесь же — словари причин/решений и разбор.
// Никаких серверных изменений: история живёт в контекстном чате платформы.
// ============================================================================

export type DisputeDecision = 'refund' | 'payout' | 'reject';
export type DisputeOpenerRole = 'client' | 'master';
export type DisputeReason = 'quality' | 'deadline' | 'payment' | 'other';

const OPEN_MARKER = 'Спор открыт';
const DECISION_MARKER = 'Спор решён';

export const DISPUTE_REASONS: Array<{ value: DisputeReason; label: string }> = [
  { value: 'quality', label: 'Качество работ' },
  { value: 'deadline', label: 'Нарушены сроки' },
  { value: 'payment', label: 'Вопрос по оплате' },
  { value: 'other', label: 'Другое' },
];

export const DISPUTE_DECISIONS: Array<{
  value: DisputeDecision;
  label: string;
  hint: string;
}> = [
  {
    value: 'refund',
    label: 'Возврат стоимости взятия',
    hint: 'Платформа вернёт мастеру стоимость взятия заявки (сумму определяет сервер).',
  },
  {
    value: 'payout',
    label: 'Выплата мастеру',
    hint: 'Спор решён в пользу мастера: стоимость взятия не возвращается, расчёт за работу — между сторонами.',
  },
  {
    value: 'reject',
    label: 'Отказ',
    hint: 'Оснований для возврата нет: решение фиксируется в истории, деньги не двигаются.',
  },
];

export const DISPUTE_DECISION_LABELS: Record<DisputeDecision, string> = {
  refund: 'возврат стоимости взятия',
  payout: 'выплата мастеру',
  reject: 'отказ',
};

export function disputeReasonLabel(reason: string): string {
  return DISPUTE_REASONS.find((r) => r.value === reason)?.label || reason;
}

export function disputeRoleLabel(role: DisputeOpenerRole): string {
  return role === 'master' ? 'мастер' : 'заказчик';
}

function reasonFromLabel(label: string): string {
  const found = DISPUTE_REASONS.find((r) => r.label === label);
  return found ? found.value : label;
}

function decisionFromLabel(label: string): DisputeDecision | null {
  const found = (Object.entries(DISPUTE_DECISION_LABELS) as Array<[DisputeDecision, string]>)
    .find(([, value]) => value === label);
  return found ? found[0] : null;
}

export interface DisputeMarker {
  kind: 'opened' | 'decision';
  role?: DisputeOpenerRole;
  reason?: string;
  comment?: string;
  decision?: DisputeDecision;
}

/** Разбор служебной строки спора (обычный комментарий → null). */
export function parseDisputeMarker(text: string): DisputeMarker | null {
  const value = String(text || '');
  if (value.startsWith(`${OPEN_MARKER} (`)) {
    const newlineAt = value.indexOf('\n');
    // Метка без комментария (одна строка) тоже распознаётся — иначе открытие
    // «терялось» бы и статус оставался «не открыт».
    const head = newlineAt >= 0 ? value.slice(0, newlineAt) : value;
    const rest = newlineAt >= 0 ? value.slice(newlineAt + 1) : '';
    const m = head.match(/^Спор открыт \((заказчик|мастер)\): (.+)$/);
    if (!m) return null;
    return {
      kind: 'opened',
      role: m[1] === 'мастер' ? 'master' : 'client',
      reason: reasonFromLabel(m[2].trim()),
      comment: rest.replace(/^Комментарий:\s*/i, '').trim(),
    };
  }
  if (value.startsWith(`${DECISION_MARKER} (`)) {
    const m = value.match(/^Спор решён \(([^)]+)\):\s*([\s\S]*)$/);
    if (!m) return null;
    const decision = decisionFromLabel(m[1].trim());
    if (!decision) return null;
    return { kind: 'decision', decision, comment: m[2].trim() };
  }
  return null;
}

export function buildOpenMessage(role: DisputeOpenerRole, reason: DisputeReason, comment: string): string {
  return `${OPEN_MARKER} (${disputeRoleLabel(role)}): ${disputeReasonLabel(reason)}\nКомментарий: ${comment.trim()}`;
}

export function buildDecisionMessage(decision: DisputeDecision, comment: string): string {
  return `${DECISION_MARKER} (${DISPUTE_DECISION_LABELS[decision]}): ${comment.trim()}`;
}
